import React, { useState } from 'react';
import { Form, Input, Select, Button, Switch, Spin, Tag, Popover } from 'antd';
import {
  EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, PlusOutlined,
  FolderOpenOutlined, TagOutlined, AppstoreOutlined, CheckCircleOutlined,
  DollarOutlined, FileTextOutlined, CalendarOutlined, ClockCircleOutlined, UserOutlined,
} from '@ant-design/icons';
import GuardedButton from '../../components/guardedButton';
import { AiTwotoneFolderOpen } from "react-icons/ai";

const MAX_INLINE_DOC_TAGS =1;

interface ServiceDetailPanelProps {
  detail: any;
  loading: boolean;
  isEditing: boolean;
  canEdit: boolean;
  canDelete: boolean;
  saving: boolean;
  form: any;
  categoryOptions: Array<{ label: string; value: any }>;
  documents: string[];
  onDocAdd: (value: string) => void;
  onDocRemove: (index: number) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  onToggleStatus: (checked: boolean) => void;
  statusLoading?: boolean;
}

const parseDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const num = Number(v);
  if (!Number.isNaN(num) && typeof v !== 'object') {
    return String(v).length === 10 ? new Date(num * 1000) : new Date(num);
  }
  return new Date(String(v));
};

const fmtDateTime = (v: any) => {
  const d = parseDate(v);
  return d ? d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null;
};

const typeChipStyle = (type?: string): React.CSSProperties => {
  switch ((type || '').toUpperCase()) {
    case 'TRAINING': return { background: '#e6f7ff', color: '#1890ff', border: '1px solid #77797a', borderRadius: '6px' };
    case 'SERVICE': return { background: '#f6ffed', color: '#52c41a', border: '1px solid #77797a', borderRadius: '6px' };
    default: return { background: '#f3f4f6', color: '#6b7280', borderRadius: '6px' };
  }
};

const personName = (p: any) => (typeof p === 'object' && p !== null ? (p.name ?? `${p.firstName ?? ''}${p.lastName ? ' ' + p.lastName : ''}`.trim()) : p);

const Field: React.FC<{ icon?: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="cat-field">
      <span className="cat-field-label">{icon}{label}</span>
      <span className="cat-field-value">{isEmpty ? <span className="cat-muted">—</span> : value}</span>
    </div>
  );
};

const ServiceDetailPanel: React.FC<ServiceDetailPanelProps> = ({
  detail, loading, isEditing, canEdit, canDelete, saving, form, categoryOptions,
  documents, onDocAdd, onDocRemove,
  onEdit, onCancel, onSave, onDelete, onToggleStatus, statusLoading,
}) => {
  const active = !!detail?.isActive;
  const categoryName = detail?.category?.categoryName ?? detail?.category;
  const categoryType = detail?.category?.categoryType ?? detail?.categoryType;
  const docsView: string[] = Array.isArray(detail?.requiredDocuments) ? detail.requiredDocuments : [];
  const createdBy = personName(detail?.createdByUser ?? detail?.createdBy);
  const updatedBy = personName(detail?.updatedByUser ?? detail?.updatedBy);

  const [docInput, setDocInput] = useState('');
  const handleAddDoc = () => {
    const value = docInput.trim();
    if (!value) return;
    onDocAdd(value);
    setDocInput('');
  };

  const statusPill = (
    <span className={`batch-status-pill batch-status-pill--${active ? 'active' : 'inactive'}`} style={{border:"1px solid #77797a"}}>
      <span className="batch-status-dot" />{active ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <div className="cat-panel">
      <div className="cat-card">
        <div className="cat-card-head">
          <div className="cat-head-left">
            <div className="cat-head-icon"><AiTwotoneFolderOpen size={35} /></div>
            <div className="cat-head-titles">
              <div className="cat-head-name">{detail?.serviceName || detail?.name || '—'}</div>
              <div className="cat-head-meta">
                {categoryName && <span className="cat-head-code"><FolderOpenOutlined /> {categoryName}</span>}
              </div>
            </div>
          </div>

          <div className="cat-head-actions" onClick={(e) => e.stopPropagation()}>
            
            {!isEditing ? (
              <>
                {canEdit && (
                  <GuardedButton className="cat-btn cat-btn-edit" icon={<EditOutlined />} onClick={onEdit}>Edit</GuardedButton>
                )}
                {canDelete && (
                  <GuardedButton className="cat-btn cat-btn-delete" danger icon={<DeleteOutlined />} onClick={onDelete}>Delete</GuardedButton>
                )}
              </>
            ) : (
              <>
                <Button className="cat-btn" icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>
                <Button className="cat-btn" type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>Save Changes</Button>
              </>
            )}
          </div>
        </div>

        <div className="cat-card-body">
          {loading ? (
            <div className="cat-loading"><Spin /></div>
          ) : isEditing ? (
            <Form form={form} layout="vertical" className="cat-form">
              <div className="cat-section">
                <div className="cat-section-title">Service Information</div>
                <div className="cat-grid cat-grid--3">
                  <Form.Item name="serviceName" label="Service" rules={[{ required: true, message: 'Please enter service name' }]}>
                    <Input placeholder="Enter service name" />
                  </Form.Item>
                  <Form.Item name="shortName" label="Short Name" rules={[{ required: true, message: 'Please enter short name' }]}>
                    <Input placeholder="Enter short name" />
                  </Form.Item>
                  <Form.Item name="price" label="Price" rules={[{ required: true, message: 'Please enter price' }]}>
                    <Input type="number" placeholder="Enter price" prefix="₹" />
                  </Form.Item>
                  <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Please select type' }]}>
                    <Select
                      placeholder="Select type"
                      options={[{ label: 'Service', value: 'SERVICE' }, { label: 'Training', value: 'TRAINING' }]}
                    />
                  </Form.Item>
                  <Form.Item name="categoryId" label="Category" rules={[{ required: true, message: 'Please select category' }]}>
                    <Select placeholder="Select category" options={categoryOptions} showSearch optionFilterProp="label" />
                  </Form.Item>
                  <Form.Item label="Status">
                    <Switch
                      checked={active}
                      loading={statusLoading}
                      onChange={onToggleStatus}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Required Documents"
                    className="cat-doc-form-item"
                    style={{ gridColumn: 'span 2', minWidth: 0 }}
                  >
                    <Input.Search
                      placeholder="Enter document name"
                      enterButton={<><PlusOutlined /> Add</>}
                      value={docInput}
                      onChange={(e) => setDocInput(e.target.value)}
                      onSearch={() => handleAddDoc()}
                    />
                    <div className="cat-docs-tags">
                      {documents.length > 0 ? (
                        <>
                          {documents.slice(0, MAX_INLINE_DOC_TAGS).map((doc, index) => (
                            <Tag
                              key={index}
                              color="blue"
                              closable
                              onClose={(e) => { e.preventDefault(); onDocRemove(index); }}
                              style={{ border: '1px solid #77797a', borderRadius: '6px' }}
                              className="cat-doc-tag"
                            >
                              <FileTextOutlined /> {doc}
                            </Tag>
                          ))}
                          {documents.length > MAX_INLINE_DOC_TAGS && (
                            <Popover
                              trigger="hover"
                              title="More documents"
                              content={
                                <div className="cat-docs-tags cat-docs-popover">
                                  {documents.slice(MAX_INLINE_DOC_TAGS).map((doc, i) => {
                                    const realIndex = MAX_INLINE_DOC_TAGS + i;
                                    return (
                                      <Tag
                                        key={realIndex}
                                        color="blue"
                                        closable
                                        onClose={(e) => { e.preventDefault(); onDocRemove(realIndex); }}
                                        style={{ border: '1px solid #77797a', borderRadius: '16px' }}
                                        className="cat-doc-tag"
                                      >
                                        <FileTextOutlined /> {doc}
                                      </Tag>
                                    );
                                  })}
                                </div>
                              }
                            >
                              <Tag className="cat-doc-tag cat-doc-more" style={{ cursor: 'pointer', border: '1px solid #77797a' }}>
                                +{documents.length - MAX_INLINE_DOC_TAGS} more
                              </Tag>
                            </Popover>
                          )}
                        </>
                      ) : (
                        <span className="cat-muted">No documents added yet</span>
                      )}
                    </div>
                  </Form.Item>
                </div>
              </div>
            </Form>
          ) : (
            <>
              <div className="cat-section" style={{  padding: '18px',  borderTop: '1px solid #f1f3f6' }}>
                <div className="cat-grid cat-grid--3">
                  <Field icon={<TagOutlined />} label="Service :" value={detail?.serviceName || detail?.name} />
                  <Field icon={<TagOutlined />} label="Short Name :" value={detail?.shortName} />
                  <Field icon={<DollarOutlined />} label="Price :" value={`₹${detail?.price ?? 0}`} />
                  <Field icon={<AppstoreOutlined />} label="Type :" value={detail?.type ? 
                   <span className="cat-chip" style={typeChipStyle(detail.type)}>{detail.type}</span> : null} />
                  <Field icon={<FolderOpenOutlined />} label="Category :" value={categoryName} />
                  <Field icon={<CheckCircleOutlined />} label="Status :" value={statusPill} />
                  <div className="cat-field" style={{ gridColumn: 'span 2', minWidth: 0, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <span className="cat-field-label" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      <FileTextOutlined /> Required Documents :
                    </span>
                    <span className="cat-field-value" style={{ flex: 1, minWidth: 0 }}>
                      {docsView.length > 0 ? (
                        <div className="cat-docs" style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                          {docsView.slice(0, MAX_INLINE_DOC_TAGS).map((doc, index) => (
                            <Tag key={index} color="blue" style={{ border: '1px solid #77797a', borderRadius:'6px', margin: 0 }} className="cat-doc-tag">
                              <FileTextOutlined /> {doc}
                            </Tag>
                          ))}
                          {docsView.length > MAX_INLINE_DOC_TAGS && (
                            <Popover
                              trigger="hover"
                              title="More documents"
                              content={
                                <div className="cat-docs cat-docs-popover">
                                  {docsView.slice(MAX_INLINE_DOC_TAGS).map((doc, i) => (
                                    <Tag key={MAX_INLINE_DOC_TAGS + i} color="blue" style={{ border: '1px solid #77797a', borderRadius:'6px' }} className="cat-doc-tag">
                                      <FileTextOutlined /> {doc}
                                    </Tag>
                                  ))}
                                </div>
                              }
                            >
                              <Tag className="cat-doc-tag cat-doc-more" style={{ cursor: 'pointer', border: '1px solid #77797a', borderRadius:'6px', margin: 0 }}>
                                +{docsView.length - MAX_INLINE_DOC_TAGS} more
                              </Tag>
                            </Popover>
                          )}
                        </div>
                      ) : (
                        <span className="cat-muted">—</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="cat-section" style={{ borderTop: '1px solid #e5e8ee', padding: '18px' }}>
                <div className="cat-grid cat-grid--3">
                  <Field icon={<UserOutlined />} label="Created By :" value={createdBy} />
                  <Field icon={<CalendarOutlined />} label="Created At :" value={fmtDateTime(detail?.createdAt)} />
                  <Field icon={<UserOutlined />} label="Updated By :" value={updatedBy} />
                  <Field icon={<ClockCircleOutlined />} label="Updated At :" value={fmtDateTime(detail?.updatedAt)} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPanel;
